import { userQueryKeys } from '@/config/queryKeys';
import { checkAndRedirectForOrgAdminAccess } from '@/helpers';
import { hasAnyOrgRole } from '@/helpers/roles';
import { fetchAllUsers, fetchUserStudies } from '@/services/users';
import { useAppStore } from '@/stores/app';
import { SUPER_ADMIN_ROLE } from '@/types';
import { createFileRoute, redirect } from '@tanstack/react-router';

// SuperAdmin-only, unlike the Users List (POR-282): this loader reads GET /users, which the
// backend itself restricts to Super Users, so widening this gate would 403 rather than render.
const allowedRoles = [SUPER_ADMIN_ROLE];

export const Route = createFileRoute('/users/$userId')({
  beforeLoad: ({ context, location }) => {
    const store = useAppStore.getState();
    const selectedOrganization = store.selectedOrganization || store.selectedOrgStub;
    checkAndRedirectForOrgAdminAccess({
      context,
      location,
      selectedOrganization,
      adminRoles: allowedRoles,
      redirect,
    });
  },
  loader: ({ context, params: { userId } }) => {
    // beforeLoad returns without throwing while auth is still PENDING, so the loader can still
    // run in that window — gate the actual fetch here too (issue #396, same as /instruments).
    const store = useAppStore.getState();
    const selectedOrganization = store.selectedOrganization || store.selectedOrgStub;
    if (
      context.authentication.status !== 'AUTHENTICATED' ||
      !selectedOrganization?.id ||
      !hasAnyOrgRole(context.authentication.user?.assumed_roles, selectedOrganization.id, allowedRoles)
    ) {
      return;
    }

    const { queryClient } = context;
    // Prefetch only: the component's own isError/isPending branches own all surfacing, so a
    // rejection here must be swallowed rather than left as an unhandled rejection.
    // No staleTime on the list — see useUserDetail's note on stale-list false negatives.
    void queryClient
      .ensureQueryData({
        queryKey: userQueryKeys.list(),
        queryFn: fetchAllUsers,
      })
      .catch(() => undefined);
    void queryClient
      .ensureQueryData({
        queryKey: userQueryKeys.studies(userId),
        queryFn: () => fetchUserStudies(userId),
        staleTime: 5 * 60 * 1000,
      })
      .catch(() => undefined);
  },
});
